import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  ChevronRight,
  Clock,
  Contact,
  DollarSign,
  Download,
  FileText,
  type LucideIcon,
  Mail,
  PieChart,
  ShieldCheck,
  Signature,
  Sparkles,
  Users,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/common/page-header";
import {
  DEFERRED_CARDS,
  REPORT_CATALOG,
  REPORT_GROUPS,
} from "@/features/reports/report-catalog";
import type { ReportDef, ReportGroup } from "@/features/reports/types";
import { cn } from "@/lib/utils";

const RANGES = ["30 days", "Quarter", "YTD", "Custom"] as const;

type Tone = "info" | "warning" | "success";
const TONE_WELL: Record<Tone, string> = {
  info: "bg-info-soft text-info",
  warning: "bg-warning-soft text-warning",
  success: "bg-success-soft text-success",
};

/** Per-report hub-card icon + well tone (keyed by catalog slug). */
const REPORT_ICONS: Record<string, { icon: LucideIcon; tone: Tone }> = {
  "upcoming-cseds": { icon: Clock, tone: "warning" },
  "csed-mismatch": { icon: ArrowLeftRight, tone: "info" },
  "unfiled-returns": { icon: FileText, tone: "info" },
  "outstanding-balance": { icon: DollarSign, tone: "info" },
  "delinquent-passports": { icon: Contact, tone: "info" },
  "cdp-finder": { icon: Mail, tone: "info" },
  kwong: { icon: PieChart, tone: "info" },
  "fta-finder": { icon: Sparkles, tone: "success" },
  "accounts-receivable": { icon: BarChart3, tone: "info" },
  "caf-monitoring": { icon: ShieldCheck, tone: "info" },
  "transcript-monitoring": { icon: FileText, tone: "info" },
  "practitioner-allocation": { icon: Users, tone: "info" },
  esignatures: { icon: Signature, tone: "info" },
};

/** Category tabs over the catalog; `heading` is the per-group section label. */
const CATEGORIES: { key: "all" | ReportGroup; tab: string; heading: string | null }[] = [
  { key: "all", tab: "All", heading: null },
  { key: "Compliance & Collections", tab: "Compliance", heading: "Compliance & Collections" },
  { key: "Financial", tab: "Financial", heading: "Financial" },
  { key: "Monitoring", tab: "Monitoring", heading: "Monitoring" },
  { key: "Firm / Practitioner", tab: "Firm", heading: "Firm & Practitioner" },
];

/** KPI chart panel: title + context + "Open report" link + big figure + chart. */
function ChartPanel({
  title,
  sub,
  to,
  kpi,
  delta,
  legend,
  children,
}: {
  title: string;
  sub: string;
  to: string;
  kpi: string;
  delta: ReactNode;
  legend: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="gap-3.5 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="font-heading text-[17px] font-semibold tracking-[-0.01em]">{title}</h3>
          <span className="text-muted-foreground text-xs">{sub}</span>
        </div>
        <Link to={to} className="text-info text-xs font-semibold whitespace-nowrap hover:underline">
          Open report
        </Link>
      </div>
      <div className="flex items-baseline gap-2.5">
        <span className="font-heading text-[2.125rem] leading-none font-semibold tracking-[-0.03em] tabular-nums">
          {kpi}
        </span>
        <span className="text-muted-foreground text-[13px]">{delta}</span>
      </div>
      {children}
      <div className="text-muted-foreground flex gap-3.5 text-xs">{legend}</div>
    </Card>
  );
}

function LegendDot({ className, round }: { className: string; round?: boolean }) {
  return (
    <span
      className={cn("inline-block size-2.5", round ? "rounded-full" : "rounded-[3px]", className)}
    />
  );
}

function ReportCard({ report }: { report: ReportDef }) {
  const to = report.to ?? `/reports/${report.key}`;
  const { icon: Icon, tone } = REPORT_ICONS[report.key] ?? { icon: FileText, tone: "info" as const };
  return (
    <Link to={to} className="group">
      <Card className="h-full flex-row items-center gap-3.5 rounded-lg p-4">
        <span
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-sm",
            TONE_WELL[tone],
          )}
        >
          <Icon className="size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-snug">
          <span className="font-semibold">{report.title}</span>
          <p className="text-muted-foreground text-xs">{report.description}</p>
        </div>
        <span className="bg-muted text-muted-foreground group-hover:bg-info group-hover:text-info-foreground flex size-8 shrink-0 items-center justify-center rounded-sm transition-colors">
          <ChevronRight className="size-4" />
        </span>
      </Card>
    </Link>
  );
}

function GroupSection({
  heading,
  reports,
  variant,
}: {
  heading: string;
  reports: ReportDef[];
  variant: "grid" | "column";
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <span className="text-muted-foreground text-xs font-semibold tracking-[0.1em] uppercase">
          {heading}
        </span>
        <span className="bg-border h-px flex-1" />
      </div>
      <div
        className={cn(
          variant === "grid"
            ? "grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3"
            : "flex flex-col gap-3.5",
        )}
      >
        {reports.map((report) => (
          <ReportCard key={report.key} report={report} />
        ))}
      </div>
    </section>
  );
}

export default function ReportsHubPage() {
  const allReports: ReportDef[] = [...Object.values(REPORT_CATALOG), ...DEFERRED_CARDS];
  const [range, setRange] = useState<(typeof RANGES)[number]>("Quarter");
  const [active, setActive] = useState<"all" | ReportGroup>("all");

  const reportsFor = (group: ReportGroup) => allReports.filter((r) => r.group === group);
  const headingFor = (group: ReportGroup) =>
    CATEGORIES.find((c) => c.key === group)?.heading ?? group;
  const countFor = (key: "all" | ReportGroup) =>
    key === "all" ? allReports.length : reportsFor(key).length;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Workspace · Insights"
        title="Reports & Analytics"
        subtitle={
          <>
            Read-only analytics across cases, transcripts, billing and clients —{" "}
            <strong className="text-foreground font-semibold">{allReports.length} reports</strong>,
            refreshed with every transcript pull.
          </>
        }
        actions={
          <>
            <div className="bg-muted flex items-center gap-0.5 rounded-sm p-[3px]">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  aria-pressed={range === r}
                  className={cn(
                    "h-9 rounded-[9px] px-3 text-[13px] font-medium transition-colors",
                    range === r
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
            <Button variant="outline">
              <Download /> Export pack
            </Button>
            <Button variant="cta">
              <CalendarClock /> Schedule report
            </Button>
          </>
        }
      />

      {/* KPI chart panels — sample series; no metrics-history endpoint yet. */}
      <div className="grid gap-6 lg:grid-cols-3">
        <ChartPanel
          title="Upcoming CSEDs"
          sub="Collection statutes expiring, next 6 months"
          to="/reports/upcoming-cseds"
          kpi="15"
          delta={<strong className="text-warning font-semibold">3 within 60 days</strong>}
          legend={
            <>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-primary" />
                Within 60 days
              </span>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-info" />
                Later
              </span>
            </>
          }
        >
          <svg viewBox="0 0 320 150" width="100%" className="block" role="img" aria-label="CSEDs expiring per month: Nov 2, Dec 1, Jan 4, Feb 0, Mar 3, Apr 5.">
            <line x1="8" y1="118" x2="312" y2="118" stroke="var(--border)" strokeWidth="1" />
            <line x1="8" y1="62" x2="312" y2="62" stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" />
            <rect x="22" y="78" width="28" height="40" rx="4" fill="var(--primary)" />
            <rect x="72" y="98" width="28" height="20" rx="4" fill="var(--primary)" />
            <rect x="122" y="38" width="28" height="80" rx="4" fill="var(--info)" />
            <rect x="172" y="116" width="28" height="2" rx="1" fill="var(--border)" />
            <rect x="222" y="58" width="28" height="60" rx="4" fill="var(--info)" />
            <rect x="272" y="18" width="28" height="100" rx="4" fill="var(--info)" />
            <text x="36" y="70" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--foreground)">2</text>
            <text x="86" y="90" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--foreground)">1</text>
            <text x="286" y="10" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--foreground)">5</text>
            <text x="36" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Nov</text>
            <text x="86" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Dec</text>
            <text x="136" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Jan</text>
            <text x="186" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Feb</text>
            <text x="236" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Mar</text>
            <text x="286" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Apr</text>
          </svg>
        </ChartPanel>

        <ChartPanel
          title="Accounts receivable"
          sub="Open balances by age bucket"
          to="/reports/accounts-receivable"
          kpi="$7,422"
          delta={
            <>
              <strong className="text-warning font-semibold">$472</strong> over 90 days
            </>
          }
          legend={
            <>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-info" />
                Within terms
              </span>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-primary" />
                Overdue 90+
              </span>
            </>
          }
        >
          <svg viewBox="0 0 320 150" width="100%" className="block" role="img" aria-label="Receivables by age: Current $4,200; 1–30 d $1,850; 31–60 d $900; 61–90 d $0; 90+ d $472.">
            <text x="0" y="16" fontSize="11" fill="var(--muted-foreground)">Current</text>
            <rect x="70" y="7" width="210" height="12" rx="4" fill="var(--info)" />
            <text x="288" y="17" fontSize="11" fontWeight="600" fill="var(--foreground)">$4,200</text>
            <text x="0" y="46" fontSize="11" fill="var(--muted-foreground)">1–30 d</text>
            <rect x="70" y="37" width="93" height="12" rx="4" fill="var(--info)" />
            <text x="171" y="47" fontSize="11" fontWeight="600" fill="var(--foreground)">$1,850</text>
            <text x="0" y="76" fontSize="11" fill="var(--muted-foreground)">31–60 d</text>
            <rect x="70" y="67" width="45" height="12" rx="4" fill="var(--info)" />
            <text x="123" y="77" fontSize="11" fontWeight="600" fill="var(--foreground)">$900</text>
            <text x="0" y="106" fontSize="11" fill="var(--muted-foreground)">61–90 d</text>
            <rect x="70" y="97" width="2" height="12" rx="1" fill="var(--border)" />
            <text x="80" y="107" fontSize="11" fill="var(--muted-foreground)">$0</text>
            <text x="0" y="136" fontSize="11" fill="var(--muted-foreground)">90+ d</text>
            <rect x="70" y="127" width="24" height="12" rx="4" fill="var(--primary)" />
            <text x="102" y="137" fontSize="11" fontWeight="600" fill="var(--warning)">$472</text>
          </svg>
        </ChartPanel>

        <ChartPanel
          title="Transcript monitoring"
          sub="Transcripts pulled per week, last 8 weeks"
          to="/reports/transcript-monitoring"
          kpi="62"
          delta={
            <>
              <strong className="text-success font-semibold">+41%</strong> vs prior 8 weeks
            </>
          }
          legend={
            <>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-info" round />
                Pulled
              </span>
              <span className="inline-flex items-center gap-1.5">
                <LegendDot className="bg-primary" round />
                Current week
              </span>
            </>
          }
        >
          <svg viewBox="0 0 320 150" width="100%" className="block" role="img" aria-label="Transcripts pulled per week over 8 weeks: 4, 6, 5, 9, 7, 11, 8, 12.">
            <line x1="8" y1="118" x2="312" y2="118" stroke="var(--border)" strokeWidth="1" />
            <line x1="8" y1="62" x2="312" y2="62" stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" />
            <path d="M20 86 L60 70 L100 78 L140 46 L180 62 L220 30 L260 54 L300 22 L300 118 L20 118 Z" fill="var(--info)" fillOpacity="0.08" />
            <path d="M20 86 L60 70 L100 78 L140 46 L180 62 L220 30 L260 54 L300 22" fill="none" stroke="var(--info)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {[
              [20, 86],
              [60, 70],
              [100, 78],
              [140, 46],
              [180, 62],
              [220, 30],
              [260, 54],
            ].map(([cx, cy]) => (
              <circle key={cx} cx={cx} cy={cy} r="4" fill="var(--info)" stroke="var(--card)" strokeWidth="2" />
            ))}
            <circle cx="300" cy="22" r="5" fill="var(--primary)" stroke="var(--card)" strokeWidth="2" />
            <text x="300" y="12" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--foreground)">12</text>
            <text x="20" y="100" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">4</text>
            <text x="20" y="138" textAnchor="start" fontSize="11" fill="var(--muted-foreground)">Aug 18</text>
            <text x="160" y="138" textAnchor="middle" fontSize="11" fill="var(--muted-foreground)">Sep 15</text>
            <text x="300" y="138" textAnchor="end" fontSize="11" fill="var(--muted-foreground)">This week</text>
          </svg>
        </ChartPanel>
      </div>

      {/* Report catalog */}
      <section className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-[22px] font-semibold tracking-[-0.015em]">All reports</h2>
          <nav
            aria-label="Report groups"
            className="bg-muted flex flex-wrap gap-0.5 rounded-sm p-[3px]"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setActive(c.key)}
                aria-current={active === c.key ? "page" : undefined}
                className={cn(
                  "flex h-[34px] items-center rounded-[9px] px-3 text-[13px] transition-colors",
                  active === c.key
                    ? "bg-card text-foreground font-semibold shadow-sm"
                    : "text-muted-foreground font-medium hover:text-foreground",
                )}
              >
                {c.tab} · {countFor(c.key)}
              </button>
            ))}
          </nav>
        </div>

        {active === "all" ? (
          <>
            <GroupSection
              heading="Compliance & Collections"
              reports={reportsFor("Compliance & Collections")}
              variant="grid"
            />
            <div className="grid items-start gap-6 lg:grid-cols-3">
              {REPORT_GROUPS.filter((g) => g !== "Compliance & Collections").map((g) => (
                <GroupSection
                  key={g}
                  heading={headingFor(g)}
                  reports={reportsFor(g)}
                  variant="column"
                />
              ))}
            </div>
          </>
        ) : (
          <GroupSection heading={headingFor(active)} reports={reportsFor(active)} variant="grid" />
        )}
      </section>
    </div>
  );
}
